import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate, Navigate, Link } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import TerratreeTable from './components/TerratreeTable';
import RepricerDashboard from './pages/RepricerDashboard';
import ShipmentConfirmation from './pages/ShipmentConfirmation';
import SignIn from './pages/signin';
import './App.css';
import companyLogo from './assets/img/gaiiff.png';
import terratreeLogo from './assets/img/terratree-lg.png';
import { Auth } from 'aws-amplify';

const theme = createTheme({
  palette: {
    mode: 'light',
  },
});

const AppContent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const normalizedPath = location.pathname.replace(/\/+$/, '');
  const shouldShowNavbar = normalizedPath !== '/signin';
  
  const [userName, setUserName] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean | null>(null);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        setUserName( user.attributes?.email || user.username || 'User');
        setIsAuthenticated(true);
      } catch (err) {
        console.log("User not signed in", err);
        setIsAuthenticated(false);
      }
    };

    checkAuth();
  }, []);

  const handleLogout = async () => {
    try {
      await Auth.signOut();
      setIsAuthenticated(false);
      // Redirect to Cognito logout then back to terratree.io
      window.location.href = 'https://terratree-orders-v3.auth.us-east-1.amazoncognito.com/logout?client_id=33c4glh0765flnu8m0diuqs9je&logout_uri=https://terratree.io';
    } catch (error) {
      console.log('Error signing out: ', error);
    }
  };


  if (isAuthenticated === null) {
    return null;
  }

  return (
    <>
      {shouldShowNavbar && (
          <nav className="navbar" style={{
            display: 'flex',
            flexDirection: 'column',
            backgroundColor: '#8EC58E',
            borderBottom: '1px solid #e0e0e0',
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0 1rem',
              height: '100px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <img
                  src={companyLogo}
                  alt="Global AI Internet Freedom Fund"
                  style={{ height: '40px', marginRight: '1rem' }}
                />
              </div>

              <div style={{ display: 'flex', alignItems: 'center', position: 'relative' }}>
                  <img
                    src={terratreeLogo}
                    alt="Terratree Logo"
                    style={{
                      height: '55px',
                      width: '55px',
                      objectFit: 'cover',
                      marginRight: '0.5rem',
                    }}
                  />
                  <button
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      fontSize: '1rem',
                      color: '#fff',
                    }}
                  >
                    <span style={{color: '#000'}}>{userName}</span>
                    <span style={{ marginLeft: '0.25rem', color: '#000' }}>▼</span>
                  </button>

                  {dropdownOpen && (
                    <div
                      className="dropdown"
                      style={{
                        position: 'absolute',
                        top: '100%',
                        right: 0,
                        background: '#fff',
                        border: '1px solid #ccc',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                        padding: '0.5rem 0',
                        zIndex: 1000,
                        minWidth: '120px',
                      }}
                    >
                      <button
                        onClick={handleLogout}
                        style={{
                          background: 'none',
                          border: 'none',
                          width: '100%',
                          textAlign: 'left',
                          padding: '0.5rem 1rem',
                          cursor: 'pointer',
                          color: '#333',
                        }}
                      >
                        Logout
                      </button>
                    </div>
                  )}
                </div>
            </div>

            <div style={{
              display: 'flex',
              gap: '0',
              backgroundColor: '#7AB67A',
            }}>
              <Link
                to="/"
                style={{
                  padding: '0.75rem 1.5rem',
                  textDecoration: 'none',
                  color: '#000',
                  backgroundColor: normalizedPath === '/' ? '#6AA66A' : 'transparent',
                  borderBottom: normalizedPath === '/' ? '3px solid #4A864A' : 'none',
                  fontWeight: normalizedPath === '/' ? 'bold' : 'normal',
                }}
              >
                Orders
              </Link>
              <Link
                to="/RepricerDashboard"
                style={{
                  padding: '0.75rem 1.5rem',
                  textDecoration: 'none',
                  color: '#000',
                  backgroundColor: normalizedPath.startsWith('/RepricerDashboard') ? '#6AA66A' : 'transparent',
                  borderBottom: normalizedPath.startsWith('/RepricerDashboard') ? '3px solid #4A864A' : 'none',
                  fontWeight: normalizedPath.startsWith('/RepricerDashboard') ? 'bold' : 'normal',
                }}
              >
                Repricer Dashboard
              </Link>
              <Link
                to="/shipmentconfirmation"
                style={{
                  padding: '0.75rem 1.5rem',
                  textDecoration: 'none',
                  color: '#000',
                  backgroundColor: normalizedPath === '/shipmentconfirmation' ? '#6AA66A' : 'transparent',
                  borderBottom: normalizedPath === '/shipmentconfirmation' ? '3px solid #4A864A' : 'none',
                  fontWeight: normalizedPath === '/shipmentconfirmation' ? 'bold' : 'normal',
                }}
              >
                Shipment Confirmation
              </Link>
            </div>
        </nav>
      )}

      <Routes>
      
        <Route
          path="/signin/"
          element={
            isAuthenticated ? <Navigate to="/" replace /> : <SignIn />
          }
        />
        <Route
          path="/"
          element={
            isAuthenticated ? <TerratreeTable /> : <Navigate to="/signin" replace />
          }
        />
        <Route
          path="/RepricerDashboard/*"
          element={
            isAuthenticated ? <RepricerDashboard /> : <Navigate to="/signin" replace />
          }
        />
        <Route
          path="/shipmentconfirmation"
          element={
            isAuthenticated ? <ShipmentConfirmation /> : <Navigate to="/signin" replace />
          }
        />
        <Route
          path="/signin"
          element={
            isAuthenticated ? <Navigate to="/" replace /> : <SignIn />
          }
        />
      </Routes>
    </>
  );
};

const App: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <AppContent />
      </Router>
    </ThemeProvider>
  );
};

export default App;
